import { onDocumentLoaded } from '@theme/utilities';

/**
 * Balance header.
 *
 * The section is always fixed at the top of the viewport and the top bar is
 * transparent at the page top. Once the user scrolls past a small threshold,
 * we set `data-scrolled` so CSS can flip the bar to the solid (white) color
 * scheme.
 *
 * On the homepage, an overlay logo animates from a large centered position
 * over the first hero section back to its nav position using GSAP. On both
 * mobile and desktop the intro is a 1s-delayed, time-based animation with
 * page scroll locked for the duration.
 */
class HeaderComponent extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {MediaQueryList | null} */
  #reducedMotionQuery = null;

  /** @type {any} */
  #logoTimeline = null;

  /** @type {any} */
  #logoDelayCallId = null;

  /** @type {boolean} */
  #logoIntroActive = false;

  /** @type {boolean} */
  #logoIntroFinished = false;

  /** @type {(() => void) | null} */
  #logoIntroCleanup = null;

  connectedCallback() {
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const blockSize = entry.borderBoxSize?.[0]?.blockSize ?? 0;
      const rounded = Math.round(blockSize);
      document.body.style.setProperty('--header-height', `${rounded}px`);
    });
    this.#resizeObserver.observe(this);

    document.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#updateScrolledState();

    this.#initLogoIntro();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    document.removeEventListener('scroll', this.#handleScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');

    this.#teardownLogoIntro();
  }

  #handleScroll = () => {
    if (this.#scrollRafId !== null) return;
    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrolledState();
    });
  };

  #updateScrolledState = () => {
    if (this.#logoIntroActive) {
      this.removeAttribute('data-scrolled');
      return;
    }
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    if (scrollTop > 16) {
      this.setAttribute('data-scrolled', '');
    } else {
      this.removeAttribute('data-scrolled');
    }
  };

  #initLogoIntro() {
    if (this.getAttribute('data-logo-intro-enabled') !== 'true') return;

    const intro = /** @type {HTMLElement | null} */ (this.querySelector('[data-logo-intro]'));
    const target = /** @type {HTMLElement | null} */ (this.querySelector('[data-logo-intro-target]'));
    if (!intro || !target) return;

    this.#reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (this.#reducedMotionQuery.matches) {
      this.removeAttribute('data-logo-intro-active');
      intro.hidden = true;
      return;
    }

    const scrollTop =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    if (scrollTop !== 0) {
      this.removeAttribute('data-logo-intro-active');
      intro.hidden = true;
      return;
    }

    this.setAttribute('data-logo-intro-active', '');
    this.#logoIntroActive = true;

    this.#waitForGsap().then((gsap) => {
      if (!gsap) {
        this.#finishLogoIntro(true);
        return;
      }
      this.#startLogoIntro(gsap, intro, target);
    });
  }

  /**
   * @returns {Promise<any>}
   */
  #waitForGsap() {
    return new Promise((resolve) => {
      const check = () => {
        const g = /** @type {any} */ (window).gsap;
        if (g) {
          resolve(g);
          return true;
        }
        return false;
      };

      if (check()) return;

      let attempts = 0;
      const id = window.setInterval(() => {
        attempts += 1;
        if (check() || attempts > 100) {
          window.clearInterval(id);
          if (attempts > 100) resolve(null);
        }
      }, 50);
    });
  }

  /**
   * @param {any} gsap
   * @param {HTMLElement} intro
   * @param {HTMLElement} target
   */
  #startLogoIntro(gsap, intro, target) {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    const resolveHero = () =>
      document.querySelector('#MainContent .webster-hero-section') ||
      document.querySelector('main .webster-hero-section') ||
      document.querySelector('#MainContent > *');

    const positionOverlay = () => {
      const hero = resolveHero();
      if (hero instanceof HTMLElement) {
        const rect = hero.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          intro.style.left = `${centerX}px`;
          intro.style.top = `${centerY}px`;
          return;
        }
      }
      intro.style.left = '';
      intro.style.top = '';
    };

    const primeIntro = () => {
      gsap.set(intro, { xPercent: -50, yPercent: -50, x: 0, y: 0, scale: 1, autoAlpha: 1 });
    };

    const computeDeltas = () => {
      positionOverlay();
      primeIntro();
      const introRect = intro.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      if (introRect.width === 0 || targetRect.width === 0) {
        return null;
      }
      const scale = targetRect.height / introRect.height;
      const introCenterX = introRect.left + introRect.width / 2;
      const introCenterY = introRect.top + introRect.height / 2;
      const targetCenterX = targetRect.left + targetRect.width / 2;
      const targetCenterY = targetRect.top + targetRect.height / 2;
      return {
        dx: targetCenterX - introCenterX,
        dy: targetCenterY - introCenterY,
        scale,
      };
    };

    const deltas = computeDeltas();
    if (!deltas) {
      this.#finishLogoIntro(true);
      return;
    }

    gsap.set(intro, {
      xPercent: -50,
      yPercent: -50,
      x: 0,
      y: 0,
      scale: 1,
      autoAlpha: 1,
      force3D: true,
    });

    const tl = gsap.timeline({ paused: true });
    tl.to(intro, {
      x: deltas.dx,
      y: deltas.dy,
      scale: deltas.scale,
      ease: 'power2.inOut',
      duration: 1,
    });
    this.#logoTimeline = tl;

    document.documentElement.classList.add('logo-intro-locked');
    this.#logoDelayCallId = gsap.delayedCall(1, () => {
      this.#logoDelayCallId = null;
      tl.eventCallback('onComplete', () => {
        document.documentElement.classList.remove('logo-intro-locked');
        this.#finishLogoIntro();
      });
      tl.play();
    });

    const startBreakpoint = isMobile;
    let resizeTimer = /** @type {number | null} */ (null);
    const onResize = () => {
      if (this.#logoIntroFinished) return;
      if (resizeTimer !== null) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        resizeTimer = null;
        if (this.#logoIntroFinished) return;

        const nowMobile = window.matchMedia('(max-width: 749px)').matches;
        if (nowMobile !== startBreakpoint) {
          this.#finishLogoIntro();
          return;
        }

        const next = computeDeltas();
        if (!next) return;
        tl.clear();
        tl.to(intro, {
          x: next.dx,
          y: next.dy,
          scale: next.scale,
          ease: 'power2.inOut',
          duration: 1,
        });
      }, 150);
    };
    window.addEventListener('resize', onResize, { passive: true });
    this.#logoIntroCleanup = () => {
      if (resizeTimer !== null) {
        window.clearTimeout(resizeTimer);
        resizeTimer = null;
      }
      window.removeEventListener('resize', onResize);
    };

    this.#attachSkipListeners();
  }

  #attachSkipListeners() {
    const skipSelector = [
      '[data-header-panel-toggle]',
      '[data-header-panel-gender-open]',
      '[data-search-toggle]',
      '.header-actions__search',
      '.header-actions__cart',
      'cart-drawer-component a',
      'cart-drawer-component button',
      '.search-action button',
    ].join(',');

    /** @param {Event} event */
    const onPointerDown = (event) => {
      if (this.#logoIntroFinished) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(skipSelector)) {
        this.#finishLogoIntro();
      }
    };

    const onSkipEvent = () => this.#finishLogoIntro();

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', (event) => {
      if (this.#logoIntroFinished) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(skipSelector)) this.#finishLogoIntro();
    }, true);
    window.addEventListener('logo-intro:skip', onSkipEvent);

    const prevCleanup = this.#logoIntroCleanup;
    this.#logoIntroCleanup = () => {
      if (prevCleanup) prevCleanup();
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('logo-intro:skip', onSkipEvent);
    };
  }

  /**
   * @param {boolean} [silent]
   */
  #finishLogoIntro(silent) {
    if (this.#logoIntroFinished) return;
    this.#logoIntroFinished = true;

    if (this.#logoDelayCallId && typeof this.#logoDelayCallId.kill === 'function') {
      this.#logoDelayCallId.kill();
    }
    this.#logoDelayCallId = null;

    if (this.#logoTimeline && !silent) {
      try {
        this.#logoTimeline.progress(1);
      } catch (_) {
        /* ignore */
      }
    }
    if (this.#logoTimeline && typeof this.#logoTimeline.kill === 'function') {
      this.#logoTimeline.kill();
    }
    this.#logoTimeline = null;

    document.documentElement.classList.remove('logo-intro-locked');
    this.removeAttribute('data-logo-intro-active');
    this.#logoIntroActive = false;

    const intro = /** @type {HTMLElement | null} */ (this.querySelector('[data-logo-intro]'));
    if (intro) {
      requestAnimationFrame(() => {
        intro.hidden = true;
      });
    }

    this.#updateScrolledState();
  }

  #teardownLogoIntro() {
    if (this.#logoIntroCleanup) this.#logoIntroCleanup();
    this.#logoIntroCleanup = null;
    this.#finishLogoIntro(true);
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update --header-group-height on resize of any child of header-group.
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        return totalHeight + (entry.borderBoxSize?.[0]?.blockSize ?? 0);
      }, 0);
      const rounded = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${rounded}px`);
    });

    if (header instanceof HTMLElement) resizeObserver.observe(header);

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) resizeObserver.observe(element);
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
